// casePrefetch.js
// Laadt van de vorige en volgende case (vanuit de CaseDetailDrawer) alvast de
// scans en het IOS Form-pdf in een kleine cache, zodat ze bij Next/Previous
// direct klaarstaan.
//
// Spelregels:
// - Alleen de ruwe bytes (Blob/Uint8Array), géén decode: dat blijft bij de
//   viewer zelf, dus geen main-thread-werk voor een case die je niet ziet.
// - Sleutel = exact het bestand (organisatie + fileLink, of de volledige
//   proxy-url). Een viewer krijgt dus nooit iets anders dan precies het
//   bestand dat 'ie zelf zou downloaden — nooit dat van een andere case.
// - Pas starten als de huidige case helemaal binnen is (start=true), één
//   download tegelijk, en direct afbreken/pauzeren zodra de huidige case weer
//   iets moet downloaden (beginForeground) of er van case gewisseld wordt.
// - Alles buiten {vorige, huidige, volgende} wordt meteen opgeruimd; bij het
//   sluiten van de drawer alles.
import apiClient from "./apiClient";
import { CASE_PREFETCH } from "../featureFlags";

// Schakelaar staat in featureFlags.js; hier opnieuw geëxporteerd voor de
// bestaande imports
export { CASE_PREFETCH };

// Rust na "huidige case klaar" (of na de laatste eigen download) voordat de
// prefetch (weer) begint
const START_DELAY_MS = 500;
// Kleine pauze tussen twee prefetch-downloads
const GAP_MS = 150;

// key -> { key, caseId, fetch, promise, controller, done, claimed }
const entries = new Map();
let queue = [];
let running = null;
let started = false;
let currentCaseId = null;
let fgCount = 0;
let pumpTimer = null;
// Sleutels die in de cache mogen: {vorige, huidige, volgende} plus het
// venster van de case waar je net vandaan kwam. Zonder dat laatste werd bij
// heen-en-weer tussen A en B steeds de buiten-buur (A-1 resp. C) weggegooid
// en daarna opnieuw gedownload. Max. 4 cases bij doorbladeren.
let keepKeys = new Set();
let windowKeys = new Set();
let prevWindowKeys = new Set();

const orgId = () => localStorage.getItem("organization_id") || "";
const scanKey = (fileLink) => `scan|${orgId()}|${fileLink}`;
const pdfKey = (url) => `pdf|${url}`;

// --- welke bestanden laadt de viewer als eerste voor een case -------------

// Zelfde volgorde als SCAN_TYPE_PRIORITY/buildGroups in Dracoloader: daar
// wordt de eerste groep met een boven- en de eerste met een onderkaak
// getoond. Wijkt dit ooit af, dan mist alleen de cache (sleutels zijn exact).
const SCAN_TYPE_PRIORITY = ["preparationScan", "prePreparationScan", "abutmentScan", "dentureScan", "ReportScan"];
const priority = (type) => {
  const i = SCAN_TYPE_PRIORITY.indexOf(type);
  return i === -1 ? SCAN_TYPE_PRIORITY.length : i;
};

export function initialScanLinks(row) {
  if (!row?.stlfiles) return [];
  let files;
  try { files = JSON.parse(row.stlfiles); } catch (e) { return []; }
  if (!Array.isArray(files)) return [];
  const previews = files
    .filter((f) => f?.fileType === "preview" && f.fileLink)
    .map((f, idx) => ({ ...f, idx }))
    // stabiel sorteren, net als Array.prototype.sort in buildGroups
    .sort((a, b) => priority(a.type) - priority(b.type) || a.idx - b.idx);
  const upper = previews.find((f) => f.jaw === "upper");
  const lower = previews.find((f) => f.jaw === "lower");
  return [upper?.fileLink, lower?.fileLink].filter(Boolean);
}

// --- downloads ------------------------------------------------------------

// Zelfde endpoint als downloadSingleFile, maar afbreekbaar en met de
// base64-decode via fetch(data:) i.p.v. een JS-lus op de main thread
async function fetchScan(fileLink, signal) {
  const res = await apiClient.post("/files/bulk-download-stream", {
    organizationId: parseInt(localStorage.getItem("organization_id")),
    files: [{ fileLink, filename: fileLink.split("/").pop() }],
  }, { responseType: "json", signal });
  const file = res.data?.files?.[0];
  if (!file?.data) return null;
  const type = file.contentType || "application/octet-stream";
  return (await fetch(`data:${type};base64,${file.data}`, { signal })).blob();
}

async function fetchPdf(url, signal) {
  const token = localStorage.getItem("token");
  const res = await fetch(url, { signal, headers: token ? { Authorization: `Bearer ${token}` } : undefined });
  if (!res.ok) return null;
  return new Uint8Array(await res.arrayBuffer());
}

function abortEntry(entry) {
  entry.controller.abort();
  entries.delete(entry.key);
  if (running === entry) running = null;
}

function schedulePump(delay) {
  clearTimeout(pumpTimer);
  pumpTimer = setTimeout(pump, delay);
}

function pump() {
  if (running || !started || fgCount > 0) return;
  const job = queue.shift();
  if (!job) return;
  if (entries.has(job.key)) { schedulePump(0); return; }
  const controller = new AbortController();
  const entry = { key: job.key, caseId: job.caseId, fetch: job.fetch, controller, done: false, claimed: false };
  entry.promise = job.fetch(controller.signal)
    .catch(() => null)
    .then((value) => {
      if (controller.signal.aborted) return null;
      entry.done = true;
      // Mislukt: niet bewaren, de viewer downloadt dan gewoon zelf
      if (!value && entries.get(job.key) === entry) entries.delete(job.key);
      return value;
    })
    .finally(() => {
      if (running === entry) {
        running = null;
        schedulePump(GAP_MS);
      }
    });
  entries.set(job.key, entry);
  running = entry;
}

// --- publieke api ----------------------------------------------------------

/**
 * Venster bijwerken. current/neighbors: { caseId, scans: [fileLink], pdfUrl }.
 * neighbors in volgorde van belang (eerst de waarschijnlijkste volgende).
 * start: huidige case is klaar met laden, prefetch mag beginnen.
 */
export function updateCasePrefetch({ current, neighbors, start }) {
  if (!CASE_PREFETCH) return;
  const jobsFor = (c) => [
    ...c.scans.map((link) => ({ caseId: c.caseId, key: scanKey(link), fetch: (s) => fetchScan(link, s) })),
    ...(c.pdfUrl ? [{ caseId: c.caseId, key: pdfKey(c.pdfUrl), fetch: (s) => fetchPdf(c.pdfUrl, s) }] : []),
  ];
  const ownKeys = new Set([current, ...neighbors].filter(Boolean).flatMap(jobsFor).map((j) => j.key));
  // Alleen bij een echte case-wissel schuift het vorige venster op
  if ((current?.caseId ?? null) !== currentCaseId) prevWindowKeys = windowKeys;
  windowKeys = ownKeys;
  const keep = new Set([...ownKeys, ...prevWindowKeys]);
  keepKeys = keep;
  currentCaseId = current?.caseId ?? null;
  entries.forEach((entry) => {
    // Buiten het venster: weg. Nog niet gestart (huidige case laadt): de
    // lopende downloads van andere cases afbreken, ze komen straks terug in
    // de wachtrij. Die van de huidige case laten lopen — die gebruikt de
    // viewer zo meteen zelf.
    if (!keep.has(entry.key) || (!start && !entry.done && entry.caseId !== currentCaseId)) abortEntry(entry);
  });
  queue = neighbors.flatMap(jobsFor).filter((j) => !entries.has(j.key));
  const wasStarted = started;
  started = !!start;
  if (started && !wasStarted) schedulePump(START_DELAY_MS);
  else if (started) schedulePump(0);
  else clearTimeout(pumpTimer);
}

// Drawer dicht/unmount: alles weg
export function clearCasePrefetch() {
  clearTimeout(pumpTimer);
  entries.forEach(abortEntry);
  entries.clear();
  queue = [];
  running = null;
  started = false;
  currentCaseId = null;
  keepKeys = new Set();
  windowKeys = new Set();
  prevWindowKeys = new Set();
}

// De huidige case gaat zelf iets downloaden: prefetch van andere cases
// meteen afbreken en pauzeren tot de laatste eigen download klaar is.
export function beginForeground() {
  if (!CASE_PREFETCH) return () => {};
  fgCount += 1;
  clearTimeout(pumpTimer);
  entries.forEach((entry) => {
    if (!entry.done && !entry.claimed && entry.caseId !== currentCaseId) {
      abortEntry(entry);
      queue.unshift({ caseId: entry.caseId, key: entry.key, fetch: entry.fetch });
    }
  });
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    fgCount = Math.max(0, fgCount - 1);
    if (fgCount === 0 && started) schedulePump(START_DELAY_MS);
  };
}

// Wat de viewer zelf gedownload heeft ook bewaren, zodat je bij terugbladeren
// naar deze case (en de prefetch voor die buur) niet opnieuw hoeft te
// downloaden. Alleen binnen het venster, en nooit een bestaand item
// overschrijven.
function store(key, value) {
  if (!value || !keepKeys.has(key) || entries.has(key)) return;
  entries.set(key, {
    key, caseId: null, fetch: null, controller: new AbortController(),
    done: true, claimed: true, promise: Promise.resolve(value),
  });
  queue = queue.filter((j) => j.key !== key);
}

export function storeScan(fileLink, blob) {
  if (CASE_PREFETCH && fileLink) store(scanKey(fileLink), blob);
}

export function storePdf(url, bytes) {
  if (CASE_PREFETCH && url) store(pdfKey(url), bytes);
}

function claim(key) {
  const entry = entries.get(key);
  if (!entry) return null;
  entry.claimed = true;
  return entry.promise;
}

// Promise<Blob|null> als dit scan-bestand in de cache zit (of onderweg is),
// anders null. Bij null/mislukt: gewoon zelf downloaden.
export function getPrefetchedScan(fileLink) {
  if (!CASE_PREFETCH || !fileLink) return null;
  return claim(scanKey(fileLink));
}

// Promise<Uint8Array|null>; altijd een kopie, want pdf.js neemt de buffer
// over (transfer naar de worker) en de cache moet 'm houden voor als je
// terugbladert.
export function getPrefetchedPdf(url) {
  if (!CASE_PREFETCH || !url) return null;
  const p = claim(pdfKey(url));
  return p ? p.then((bytes) => (bytes ? bytes.slice() : null)) : null;
}
